import { isWeb } from "@helpers/platform";
import type {
	BaseItemSchema,
	Choice,
	ItemResponse,
} from "@memorang/types/src/session/new-session";
import Box from "@memorang/ui/src/components/Box";
import { useMemo } from "react";
import { sessionMaxWidth } from "../../../../constants";
import HintContainer from "../../../../containers/HintContainer";
import ChoicesList from "../../../choices/ChoicesList";

type Props = {
	inReviewMode?: boolean;
	multiSelect?: boolean;
	isSummativeTest?: boolean;
	isAnswered?: boolean;
	selectedChoiceIds?: string[];
	correctAnswerIds?: string[];
	crossedOutChoiceIds?: string[];
	choices: Choice[];
	showHint?: boolean;
	isMobile?: boolean;
	isSequence?: boolean;
	response: Extract<ItemResponse, { type: "choice" }>;
	currentItem: BaseItemSchema;
	handleSelectChoice: (choiceId: string, crossingOut?: boolean) => void;
	correctAnswerOrders?: string[];
	shouldShowCrossout?: boolean;
};

const shuffleChoices = (choices: Choice[]) => {
	return choices.sort(() => Math.random() - 0.5);
};

const MCQ = ({
	inReviewMode,
	multiSelect,
	isSummativeTest,
	isAnswered,
	selectedChoiceIds,
	correctAnswerIds,
	crossedOutChoiceIds,
	choices,
	showHint,
	isMobile,
	handleSelectChoice,
	currentItem,
	isSequence,
	response,
	correctAnswerOrders,
	shouldShowCrossout,
}: Props) => {
	const shuffle = response.shuffle;

	const shuffledChoices = useMemo(
		() => (shuffle && !inReviewMode ? shuffleChoices(choices) : choices),
		[choices, shuffle, inReviewMode],
	);
	return (
		<Box gap={16}>
			<Box
				paddingHorizontal={
					(isSummativeTest && !isMobile) || (isWeb && !isMobile) ? 0 : 16
				}
				maxWidth={isSummativeTest ? sessionMaxWidth.summative : 960}
			>
				<ChoicesList
					choices={shuffledChoices}
					useCardWrapper={isSummativeTest ? isMobile : true}
					showCrossOut={!isSummativeTest && shouldShowCrossout}
					isAnswered={isAnswered}
					selectedChoiceIds={selectedChoiceIds}
					multiSelect={multiSelect}
					correctAnswerIds={correctAnswerIds}
					handleSelectChoice={handleSelectChoice}
					crossedOutChoiceIds={crossedOutChoiceIds}
					itemId={currentItem.id!}
					maxChoiceCount={response.maxCorrectChoices}
					isSequence={isSequence}
					correctAnswerOrders={correctAnswerOrders}
				/>
			</Box>
			{showHint && choices.length > 0 && (
				<Box paddingHorizontal={isWeb && !isMobile ? 0 : 16}>
					<HintContainer key={currentItem.id} item={currentItem} />
				</Box>
			)}
		</Box>
	);
};

export default MCQ;
